/**
 * AdminSettings — the super-admin's own profile & credentials management.
 *
 * Deliberately narrower than the trader Settings page: an admin doesn't
 * need referral cards, leaderboard opt-out, or notification prefs. They
 * DO need to:
 *   - see their profile at a glance
 *   - change their password (closed-loop, via ChangePasswordCard)
 *   - flip the theme
 *   - log out
 *
 * Sits alongside the other /admin/* pages inside the shared Sidebar/AppLayout
 * shell (2026-09-05 UI Architecture Parity directive — AdminHeader retired).
 */
import { useAuth } from "@/context/AuthContext";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import { useTheme } from "@/context/ThemeProvider";
import { Sun, Moon, LogOut, KeyRound, UserCog, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import DashboardGridWrapper from "@/components/dashboard/DashboardGridWrapper";
import PageHeader from "@/components/dashboard/PageHeader";

export default function AdminSettings() {
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const nav = useNavigate();

    const widgets = [
        {
            id: "settings-profile", title: "Profile", category: "Account", layout: { x: 0, y: 0, w: 6, h: 6 }, minW: 3, minH: 4,
            render: () => (
                <div data-testid="admin-settings-profile">
                    <div className="overline mb-3 flex items-center gap-2">
                        <Shield className="w-3 h-3" /> Profile
                    </div>
                    <div className="text-sm space-y-2">
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Name</span>
                            <span>{user?.full_name || "—"}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Email</span>
                            <span className="mono text-xs">{user?.email}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Role</span>
                            <span className="uppercase font-bold text-primary">Super Admin</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Account status</span>
                            <span className="text-success">Active</span>
                        </div>
                    </div>
                    <p className="mt-4 text-[11px] text-muted-foreground leading-snug">
                        To change your name or email, run the super-admin
                        identity rotation script in the ops runbook. This
                        deliberately isn't a click-button — super-admin identity
                        changes require an out-of-band audit trail.
                    </p>
                </div>
            ),
        },
        {
            id: "settings-appearance", title: "Appearance", category: "Account", layout: { x: 6, y: 0, w: 6, h: 3 }, minW: 3, minH: 2,
            render: () => (
                <div data-testid="admin-settings-appearance">
                    <div className="overline mb-3">Appearance</div>
                    <div className="flex items-center justify-between">
                        <div className="text-sm">Theme</div>
                        <Button variant="outline" onClick={toggle} data-testid="admin-settings-theme-toggle">
                            {theme === "dark" ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                            {theme === "dark" ? "Light mode" : "Dark mode"}
                        </Button>
                    </div>
                </div>
            ),
        },
        {
            id: "settings-password", title: "Change password", category: "Security", layout: { x: 6, y: 3, w: 6, h: 6 }, minW: 3, minH: 4, noPadding: true,
            render: () => (
                <div data-testid="admin-settings-password">
                    <ChangePasswordCard />
                </div>
            ),
        },
        {
            id: "settings-session", title: "Session", category: "Security", layout: { x: 0, y: 6, w: 6, h: 5 }, minW: 3, minH: 3,
            render: () => (
                <div data-testid="admin-settings-session">
                    <div className="overline mb-3 flex items-center gap-2">
                        <KeyRound className="w-3 h-3" /> Session
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                        End this admin session on this device. You'll need to log
                        in again with your password + recovery code if you've
                        rotated your credentials.
                    </p>
                    <Button
                        variant="outline"
                        onClick={async () => { await logout(); nav("/"); }}
                        data-testid="admin-settings-logout"
                    >
                        <LogOut className="w-4 h-4 mr-2" /> Sign out
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <>
            <PageHeader
                icon={UserCog}
                eyebrow="Admin Tools"
                title="Admin Settings"
                subtitle={<>Manage the credentials attached to <span className="mono">{user?.email}</span>.</>}
                testId="admin-settings-header"
            />

            <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-settings" widgets={widgets} />
        </>
    );
}
